/**
 * ArtExportDropdown.jsx — Phase 11 (2026-02).
 *
 * Persistent header dropdown for the 5 Art Dept tabs. Each tab supplies its
 * own `options` list; the dropdown triggers a browser download via a Blob
 * response from `GET /api/projects/{pid}/art/exports/{type}?...`.
 *
 * Usage (from any ArtByX tab):
 *   <ArtExportDropdown
 *     projectId={projectId} token={token}
 *     options={[
 *       { key: 'scene_breakdown', label: 'Scene breakdown PDF',
 *         params: currentSceneId ? { scene_id: currentSceneId } : {} },
 *       ...
 *     ]}
 *   />
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Download, Loader2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { LanguageDownloadDialog } from '../../components/LanguageDownloadDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const _isEnglishLang = (lang) =>
  !lang || lang === 'english' || lang === 'en' || lang === 'en-us';

export default function ArtExportDropdown({ projectId, token, projectLanguage, options = [], className = '' }) {
  const [open, setOpen] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [langDialogOpen, setLangDialogOpen] = useState(false);
  const pendingOptRef = useRef(null);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Fire the actual download for `opt`, using `lang` ("english" | "original")
  // as the ?lang= query param. XLSX exports skip the lang param entirely
  // (matches Master Breakdown / Costume dept: language chooser is a
  // PDF-content decision, XLSX content is language-agnostic).
  const runDownload = async (opt, lang) => {
    const params = { ...(opt.params || {}) };
    if (opt.format) params.format = opt.format;
    if (opt.format !== 'xlsx' && lang) params.lang = lang;
    let url;
    let ext = 'pdf';
    if (opt.type === 'shoot_day_bundle') {
      if (!params.shoot_day && !params.scene_ids) {
        const raw = window.prompt('Enter shoot day (e.g. 1, 2, 3):');
        if (!raw) return;
        params.shoot_day = raw.trim();
      }
      const qs = new URLSearchParams(params).toString();
      url = `${API}/projects/${projectId}/art/exports-bundle/shoot_day${qs ? `?${qs}` : ''}`;
      ext = 'zip';
    } else {
      const qs = new URLSearchParams(params).toString();
      url = `${API}/projects/${projectId}/art/exports/${opt.type}${qs ? `?${qs}` : ''}`;
      ext = (opt.format === 'xlsx') ? 'xlsx' : 'pdf';
    }
    setBusyKey(opt.key);
    try {
      const r = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 90000,
      });
      const blob = new Blob([r.data], { type: r.headers['content-type'] || 'application/pdf' });
      const dl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dl;
      a.download = `${opt.type}_${new Date().toISOString().slice(0,10)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dl);
      toast.success(`${opt.label} downloaded`);
      setOpen(false);
    } catch (e) {
      console.error(e);
      const msg = e?.response?.status === 404
        ? 'No scenes match that shoot day.'
        : `Export failed: ${opt.label}`;
      toast.error(msg);
    } finally { setBusyKey(null); }
  };

  // Entry point from the menu — decides whether to prompt for language.
  const runExport = async (opt) => {
    // XLSX exports are language-agnostic; no prompt.
    // English projects: silent English download (matches Master Breakdown / Costume dept).
    if (opt.format === 'xlsx' || _isEnglishLang(projectLanguage)) {
      return runDownload(opt, 'english');
    }
    // Non-English project → open the shared LanguageDownloadDialog.
    pendingOptRef.current = opt;
    setOpen(false);
    setLangDialogOpen(true);
  };

  const onLangSelect = (lang) => {
    setLangDialogOpen(false);
    const opt = pendingOptRef.current;
    pendingOptRef.current = null;
    if (!opt) return;
    // lang here is exactly "english" | "original" — pass through as ?lang=…
    runDownload(opt, lang);
  };

  const onLangCancel = () => {
    setLangDialogOpen(false);
    pendingOptRef.current = null;
    // Aborted — no silent fallback per product spec.
  };

  return (
    <div ref={ref} className={`relative inline-block ${className}`}>
      <button
        onClick={() => setOpen(v => !v)}
        className="text-xs px-3 py-1.5 rounded bg-[var(--color-panel)] border border-[var(--color-border)] text-[var(--color-text)] hover:border-[var(--color-border-strong)] flex items-center gap-1"
        data-testid="art-export-dropdown-btn">
        <Download className="w-3 h-3" />
        Export
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 bg-[var(--color-bg)] border border-[var(--color-border)] rounded shadow-lg min-w-[240px]"
          data-testid="art-export-dropdown-menu">
          {options.map(opt => (
            <button key={opt.key}
              onClick={() => runExport(opt)}
              disabled={busyKey !== null}
              className="w-full text-left text-[11px] px-3 py-2 hover:bg-[var(--color-panel)] text-[var(--color-text)] disabled:opacity-40 flex items-center gap-2"
              data-testid={`art-export-opt-${opt.key}`}>
              {busyKey === opt.key
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <Download className="w-3 h-3 text-[var(--color-accent)]" />}
              <span className="flex-1">{opt.label}</span>
              {opt.format === 'xlsx' && (
                <span className="text-[9px] text-[var(--color-text-sec)] font-mono">.xlsx</span>
              )}
            </button>
          ))}
          {options.length === 0 && (
            <div className="text-[11px] px-3 py-2 text-[var(--color-text-dim)] italic">No exports available on this tab.</div>
          )}
        </div>
      )}
      <LanguageDownloadDialog
        open={langDialogOpen}
        projectLanguage={projectLanguage}
        onClose={onLangCancel}
        onSelect={onLangSelect}
      />
    </div>
  );
}
